/* A row open beside its table: its fields as a form (or a new row's), the rows of other tables
   linking to it, and what happened to it — who added it, who changed what, and the repeats a
   unique rule refused because this row holds their value. Save writes through the same rules
   as the table, and says what a rule refused. */
import React, { useEffect, useState } from "react";
import { Ban, GitMerge, Mail, Pencil, Plus, Trash2 } from "lucide-react";

import { useAddRows, useSpaceRow, useUpdateRows } from "@/shared/api/spaces";
import { RowList } from "@/shared/components/settings-atoms";
import { BrandButton, CloseButton } from "@/shared/components/ui-kit";
import { ago } from "@/shared/lib/time";

import { repeatedNames } from "./table/columns";
import RowFields, { type FieldValues } from "./RowFields";

import type { NavigationOptions, SpaceActivity, SpaceColumn, SpaceRowResult, SpaceTable, SpaceValue } from "@/shared/types";

export default function RowPanel({ agentId, table, tables, rowId, onOpen, onOpenRun, onClose }: {
  agentId: string;
  table: SpaceTable;
  tables: SpaceTable[];
  /** "new" for a row not saved yet. */
  rowId: string;
  /** Another row, of this table or another, opened from a link. */
  /** A new row's form becomes the row it made in place (`replace`): Back skips the form. */
  onOpen: (tableId: string, rowId: string, how?: NavigationOptions) => void;
  onOpenRun?: (runId: string) => void;
  onClose: () => void;
}) {
  const isNew = rowId === "new";
  const { data: row } = useSpaceRow(agentId, table.id, isNew ? null : rowId);
  const add = useAddRows(agentId, table.id);
  const update = useUpdateRows(agentId, table.id);
  const [values, setValues] = useState<FieldValues>({});
  const [verified, setVerified] = useState<Record<string, string>>({});
  const [changed, setChanged] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<SpaceRowResult | null>(null);

  // A different row starts the form again.
  useEffect(() => {
    setChanged(new Set());
    setResult(null);
  }, [rowId]);
  // What is saved fills the form, except what the person is changing: a run writing the row
  // meanwhile never wipes what they typed.
  useEffect(() => {
    setValues((was) => ({ ...(row?.values ?? {}), ...pick(was, changed) }));
    setVerified((was) => ({ ...(row?.verified_by ?? {}), ...pick(was, changed) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `changed` is read, not followed
  }, [row]);

  const set = (key: string, value: SpaceValue) => {
    setValues((was) => ({ ...was, [key]: value }));
    setChanged((was) => new Set(was).add(key));
  };

  const save = async () => {
    const sent = Object.fromEntries([...changed].map((key) => [key, wire(values[key])]));
    const verifiedBy = Object.fromEntries(Object.entries(verified).filter(([, by]) => by.trim()));
    try {
      const [answer] = isNew
        ? await add.mutateAsync([{ values: sent, verified_by: verifiedBy }])
        : await update.mutateAsync([{ id: rowId, values: sent, verified_by: verifiedBy }]);
      setResult(answer);
      if (answer.status === "added" || answer.status === "updated") setChanged(new Set());
      if (answer.status === "added" && answer.row_id) onOpen(table.id, answer.row_id, { replace: true });
    } catch {
      // the query cache has shown the failure; the form keeps what was typed to try again
    }
  };

  const title = String(values[table.columns[0]?.key] ?? "") || (isNew ? "New row" : "Untitled");
  const named = (tableId: string) => tables.find((t) => t.id === tableId)?.name ?? "A table";

  return (
    <aside aria-label={title} className="max-md:fixed max-md:inset-0 max-md:z-40 w-full md:w-[420px] shrink-0 min-h-0 flex flex-col md:border-l hairline bg-[var(--canvas)] slide-in-right">
      <div className="h-12 shrink-0 flex items-center gap-2 pl-4 pr-2 border-b hairline">
        <span className="flex-1 min-w-0 truncate text-[12.5px] text-[var(--text-tertiary)]">
          {table.name}
          {!isNew && row && <span className="font-mono"> · {row.id.slice(0, 8)}</span>}
        </span>
        <CloseButton onClick={onClose} aria-label="Close row" />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-5 pb-6 space-y-5">
        <div className="text-[19px] font-medium tracking-[-0.01em] text-[var(--text-primary)] truncate">{title}</div>
        <RowFields
          agentId={agentId}
          columns={table.columns}
          tables={tables}
          values={values}
          verified={verified}
          people={row?.people ?? {}}
          onValue={set}
          onVerified={(key, by) => {
            setVerified((was) => ({ ...was, [key]: by }));
            setChanged((was) => new Set(was).add(key));
          }}
        />
        {result && result.status !== "added" && result.status !== "updated" && (
          <p role="alert" className="m-0 text-[12.5px] leading-[1.5] text-[var(--danger)]">
            {result.status === "refused" ? result.problem : (
              <>
                Another row already holds this {repeatedNames(result.repeated, table.columns)}.{" "}
                {result.row_id && (
                  <button type="button" onClick={() => onOpen(table.id, result.row_id as string)} className="underline underline-offset-2">
                    Open it
                  </button>
                )}
              </>
            )}
          </p>
        )}
        {!isNew && row && row.linked_from.length > 0 && (
          <section className="space-y-2">
            <div className="section-label">Linked from</div>
            <RowList>
              {row.linked_from.map((link) => (
                <button
                  key={`${link.table_id}:${link.id}`}
                  type="button"
                  onClick={() => onOpen(link.table_id, link.id)}
                  className="press -mx-2 w-[calc(100%+16px)] flex items-center gap-3 px-2 my-1 py-1.5 rounded-[8px] text-left hover:bg-[var(--accent-hover)]"
                >
                  <span className="flex-1 min-w-0 truncate text-[13px] font-medium text-[var(--text-primary)]">{link.label || "Untitled"}</span>
                  <span className="shrink-0 text-[12px] text-[var(--text-tertiary)]">{named(link.table_id)}</span>
                </button>
              ))}
            </RowList>
          </section>
        )}
        {!isNew && row && row.activity.length > 0 && (
          <section className="space-y-2">
            <div className="section-label">Activity</div>
            <ol className="relative m-0 p-0 list-none">
              <span aria-hidden="true" className="absolute left-[10.5px] top-3 bottom-3 w-px bg-[var(--hairline-strong)]" />
              {row.activity.map((event, index) => <ActivityLine key={index} event={event} columns={table.columns} onOpenRun={onOpenRun} />)}
            </ol>
          </section>
        )}
      </div>
      <div className="shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t hairline">
        <BrandButton onClick={save} disabled={!changed.size || add.isPending || update.isPending} className="disabled:opacity-40 disabled:pointer-events-none">
          {isNew ? "Add row" : "Save changes"}
        </BrandButton>
      </div>
    </aside>
  );
}

/** A form value as the API takes it: a link as its row ids. */
function wire(value: SpaceValue | undefined): unknown {
  if (Array.isArray(value)) return value.map((linked) => linked.id);
  return value === "" ? null : value ?? null;
}

/** The entries of `values` under the keys a person changed. */
function pick<T>(values: Record<string, T>, keys: Set<string>): Record<string, T> {
  return Object.fromEntries(Object.entries(values).filter(([key]) => keys.has(key)));
}

const ACTIVITY = {
  added: { Icon: Plus, words: "added this row" },
  updated: { Icon: Pencil, words: "changed" },
  refused_repeat: { Icon: Ban, words: "tried to add it again; refused, it's already here" },
  merged: { Icon: GitMerge, words: "merged repeats into this row" },
  deleted: { Icon: Trash2, words: "deleted this row" },
} as const;

function ActivityLine({ event, columns, onOpenRun }: {
  event: SpaceActivity;
  columns: SpaceColumn[];
  onOpenRun?: (runId: string) => void;
}) {
  const { Icon, words } = ACTIVITY[event.kind] ?? { Icon: Mail, words: event.kind };
  const who = event.agent_name ?? event.user_name ?? event.user_email ?? "Someone";
  const changedNames = event.kind === "updated"
    ? Object.keys(event.changes).map((key) => columns.find((c) => c.key === key)?.name ?? key).join(", ")
    : "";
  const refused = event.kind === "refused_repeat";
  return (
    <li className="relative flex gap-2.5 py-1.5">
      <span className={`z-[1] w-[22px] h-[22px] shrink-0 grid place-items-center rounded-full ring-1 ${refused ? "bg-[var(--danger-bg)] ring-[var(--danger-ring)] text-[var(--danger)]" : "bg-[var(--canvas)] ring-[var(--hairline-strong)] text-[var(--text-tertiary)]"}`}>
        <Icon className="w-3 h-3" strokeWidth={2} />
      </span>
      <span className="min-w-0 text-[13px] leading-[1.45] text-[var(--text-secondary)]">
        <span className="font-medium text-[var(--text-primary)]">{who}</span> {words}
        {changedNames && ` ${changedNames}`}
        <span className="block font-mono text-[11.5px] text-[var(--text-quaternary)]">
          {ago(event.created_at)}
          {event.run_id && " · "}
          {event.run_id && (onOpenRun ? (
            <button type="button" onClick={() => onOpenRun(event.run_id as string)} className="underline underline-offset-2 hover:text-[var(--text-secondary)]">
              run {event.run_id.slice(0, 8)}
            </button>
          ) : `run ${event.run_id.slice(0, 8)}`)}
        </span>
      </span>
    </li>
  );
}
